import React, { useState, useEffect } from 'react';
import { Users, UserCheck, Shield, Settings, Plus, Edit2, Trash2, Search, Eye, EyeOff, School } from 'lucide-react';
import {
  getAllUsers,
  createSchoolUser,
  updateSchoolUser,
  deleteSchoolUser,
  toggleSchoolUserStatus,
  searchSchoolUsers,
  getSchoolUsersStats,
  getAllSchools,
  getAllUserFunctions,
  getCurrentUserProfile
} from '../services/usuariosService';
import '../styles/usuarios.css';

const Usuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [escolas, setEscolas] = useState([]);
  const [funcoes, setFuncoes] = useState([]);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    department: '',
    school_id: '',
    funcao_id: '',
    is_active: true
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  // Departamentos disponíveis
  const departamentos = [
    'Direção',
    'Coordenação Pedagógica',
    'Secretaria',
    'Administrativo',
    'Tecnologia da Informação',
    'Recursos Humanos',
    'Financeiro'
  ];

  // Carregar dados iniciais
  useEffect(() => {
    loadInitialData();
  }, []);

  // Buscar com filtro
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      handleSearch();
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Carregar perfil do usuário logado
      const profileResult = await getCurrentUserProfile();
      if (profileResult.success) {
        setCurrentUserProfile(profileResult.data);
      }

      // Carregar escolas baseado no perfil do usuário
      const escolasResult = await getAllSchools();
      if (escolasResult.success) {
        const todasEscolas = escolasResult.data;
        
        // Se for school admin, filtrar apenas a escola do usuário
        if (profileResult.success && profileResult.data?.school_adm && profileResult.data?.school_id) {
          const escolaDoUsuario = todasEscolas.filter(escola => escola.id === profileResult.data.school_id);
          setEscolas(escolaDoUsuario);
        } else {
          // Se for super admin, mostrar todas as escolas
          setEscolas(todasEscolas);
        }
      }

      // Carregar funções de usuários
      const funcoesResult = await getAllUserFunctions();
      if (funcoesResult.success) {
        setFuncoes(funcoesResult.data);
      }

      // Carregar usuários e estatísticas
      await loadData();
    } catch (error) {
      console.error('Erro ao carregar dados iniciais:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    try {
      const [usersResult, statsResult] = await Promise.all([
        getAllUsers(),
        getSchoolUsersStats()
      ]);

      if (usersResult.success) {
        // Filtrar apenas usuários com school_adm = true no front-end
        setUsuarios(usersResult.data.filter(user => user.school_adm === true));
      }

      if (statsResult.success) {
        setStats(statsResult.data);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    }
  };

  const handleSearch = async () => {
    try {
      const filters = {};
      
      // Se for school admin, filtrar apenas pela escola do usuário
      if (currentUserProfile?.school_adm) {
        filters.school_id = currentUserProfile.school_id;
      }

      const result = await searchSchoolUsers(searchTerm, filters);
      if (result.success) {
        setUsuarios(result.data);
      }
    } catch (error) {
      console.error('Erro na busca:', error);
    }
  };

  const openModal = (usuario = null) => {
    if (usuario) {
      setEditingUser(usuario);
      setFormData({
        name: usuario.name || '',
        email: usuario.email || '',
        password: '', // Não preencher senha na edição
        phone: usuario.phone || '',
        department: usuario.department || '',
        school_id: usuario.school_id || '',
        funcao_id: usuario.funcao_id || '',
        is_active: usuario.is_active
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        phone: '',
        department: '',
        school_id: currentUserProfile?.school_adm ? currentUserProfile.school_id : '',
        funcao_id: '',
        is_active: true
      });
    }
    setFormErrors({});
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      phone: '',
      department: '',
      school_id: currentUserProfile?.school_adm ? currentUserProfile.school_id : '',
      funcao_id: '',
      is_active: true
    });
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Nome é obrigatório';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email é obrigatório';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Email inválido';
    }

    if (!editingUser && !formData.password.trim()) {
      errors.password = 'Senha é obrigatória';
    } else if (!editingUser && formData.password.length < 6) {
      errors.password = 'Senha deve ter pelo menos 6 caracteres';
    }

    if (!formData.school_id) {
      errors.school_id = 'Escola é obrigatória';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitLoading(true);

    try {
      let result;
      
      if (editingUser) {
        result = await updateSchoolUser(editingUser.id, formData);
      } else {
        result = await createSchoolUser(formData);
      }

      if (result.success) {
        closeModal();
        loadData();
        alert(editingUser ? 'Usuário atualizado com sucesso!' : 'Usuário criado com sucesso!');
      } else {
        setFormErrors({ submit: result.error });
      }
    } catch (error) {
      setFormErrors({ submit: 'Erro interno. Tente novamente.' });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleToggleStatus = async (usuario) => {
    try {
      const result = await toggleSchoolUserStatus(usuario.id, !usuario.is_active);
      if (result.success) {
        loadData();
        alert(`Usuário ${!usuario.is_active ? 'ativado' : 'desativado'} com sucesso!`);
      } else {
        alert('Erro ao alterar status: ' + result.error);
      }
    } catch (error) {
      alert('Erro interno ao alterar status');
    }
  };

  const handleDelete = async (usuario) => {
    if (!window.confirm(`Tem certeza que deseja excluir o usuário "${usuario.name}"?\n\nEsta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      const result = await deleteSchoolUser(usuario.id);
      if (result.success) {
        loadData();
        alert('Usuário excluído com sucesso!');
      } else {
        alert('Erro ao excluir usuário: ' + result.error);
      }
    } catch (error) {
      alert('Erro interno ao excluir usuário');
    }
  };

  if (loading) {
    return (
      <div className="usuarios">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Carregando usuários...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="usuarios">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h1 className="page-title">Usuários Escolares</h1>
            <p className="page-subtitle">
              Gerenciamento de usuários administrativos das escolas
              {currentUserProfile?.school_adm && currentUserProfile?.escolas && (
                <span> - {currentUserProfile.escolas.nome}</span>
              )}
            </p>
          </div>
          <button className="btn-primary" onClick={() => openModal()}>
            <Plus size={20} />
            Novo Usuário
          </button>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-header">
            <div className="stat-icon">
              <Users size={24} />
            </div>
          </div>
          <div className="stat-value">
            {currentUserProfile?.school_adm && currentUserProfile?.school_id 
              ? usuarios.filter(u => u.school_id === currentUserProfile.school_id).length
              : stats.total
            }
          </div>
          <div className="stat-label">Total de Usuários</div>
        </div>
        <div className="stat-card">
          <div className="stat-header">
            <div className="stat-icon success">
              <UserCheck size={24} />
            </div>
          </div>
          <div className="stat-value">
            {currentUserProfile?.school_adm && currentUserProfile?.school_id 
              ? usuarios.filter(u => u.school_id === currentUserProfile.school_id && u.is_active).length
              : stats.active
            }
          </div>
          <div className="stat-label">Usuários Ativos</div>
        </div>
        <div className="stat-card">
          <div className="stat-header">
            <div className="stat-icon warning">
              <Shield size={24} />
            </div>
          </div>
          <div className="stat-value">
            {currentUserProfile?.school_adm && currentUserProfile?.school_id 
              ? usuarios.filter(u => u.school_id === currentUserProfile.school_id && !u.is_active).length
              : stats.inactive
            }
          </div>
          <div className="stat-label">Usuários Inativos</div>
        </div>
      </div>

      <div className="content-card">
        <div className="table-header">
          <div className="search-container">
            <Search size={20} />
            <input
              type="text"
              placeholder="Buscar por nome, email ou departamento..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Usuário</th>
                <th>Contato</th>
                <th>Escola</th>
                <th>Função/Departamento</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {usuarios.length === 0 ? (
                <tr>
                  <td colSpan="6" className="no-data">
                    {searchTerm ? 'Nenhum usuário encontrado' : 'Nenhum usuário cadastrado'}
                  </td>
                </tr>
              ) : (
                usuarios
                  .filter(usuario => {
                    // Se for school admin, mostrar apenas usuários da mesma escola
                    if (currentUserProfile?.school_adm && currentUserProfile?.school_id) {
                      return usuario.school_id === currentUserProfile.school_id;
                    }
                    return true;
                  })
                  .map(usuario => {
                  return (
                    <tr key={usuario.id}>
                      <td>
                        <div className="user-info">
                          <div className="user-avatar">
                            {usuario.name?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                          <div>
                            <div className="user-name">{usuario.name}</div>
                            <div className="user-email">{usuario.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="contact-info">
                          <div>{usuario.phone || '-'}</div>
                          <small>Criado em {new Date(usuario.created_at).toLocaleDateString('pt-BR')}</small>
                        </div>
                      </td>
                      <td>
                        <div className="school-info">
                          <School size={16} />
                          <span>{usuario.escolas?.nome || 'Escola não encontrada'}</span>
                        </div>
                      </td>
                      <td>
                        <div className="function-info">
                          {usuario.funcoes_usuarios ? (
                            <div className="function-badge" style={{ backgroundColor: usuario.funcoes_usuarios.cor + '20', color: usuario.funcoes_usuarios.cor }}>
                              {usuario.funcoes_usuarios.nome}
                            </div>
                          ) : (
                            <span className="no-function">Sem função</span>
                          )}
                          <small>{usuario.department || '-'}</small>
                        </div>
                      </td>
                      <td>
                        <button
                          className={`status-toggle ${usuario.is_active ? 'active' : 'inactive'}`}
                          onClick={() => handleToggleStatus(usuario)}
                          title={`Clique para ${usuario.is_active ? 'desativar' : 'ativar'}`}
                        >
                          {usuario.is_active ? 'Ativo' : 'Inativo'}
                        </button>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-action edit"
                            onClick={() => openModal(usuario)}
                            title="Editar usuário"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            className="btn-action delete"
                            onClick={() => handleDelete(usuario)}
                            title="Excluir usuário"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Formulário */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingUser ? 'Editar Usuário' : 'Novo Usuário Escolar'}</h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="name">Nome Completo *</label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className={formErrors.name ? 'error' : ''}
                    placeholder="Ex: João Silva"
                  />
                  {formErrors.name && <span className="error-message">{formErrors.name}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className={formErrors.email ? 'error' : ''}
                    placeholder="joao@escola.com"
                    disabled={editingUser} // Email não pode ser alterado
                  />
                  {formErrors.email && <span className="error-message">{formErrors.email}</span>}
                </div>

                {!editingUser && (
                  <div className="form-group">
                    <label htmlFor="password">Senha *</label>
                    <div className="password-input">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        className={formErrors.password ? 'error' : ''}
                        placeholder="Mínimo 6 caracteres"
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                    {formErrors.password && <span className="error-message">{formErrors.password}</span>}
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="phone">Telefone</label>
                  <input
                    type="tel"
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="(11) 99999-9999"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="funcao_id">Função</label>
                  <select
                    id="funcao_id"
                    value={formData.funcao_id}
                    onChange={(e) => setFormData({...formData, funcao_id: e.target.value})}
                  >
                    <option value="">Selecione uma função</option>
                    {funcoes.map(funcao => (
                      <option key={funcao.id} value={funcao.id}>{funcao.nome}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="department">Departamento</label>
                  <select
                    id="department"
                    value={formData.department}
                    onChange={(e) => setFormData({...formData, department: e.target.value})}
                  >
                    <option value="">Selecione um departamento</option>
                    {departamentos.map(dept => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="school_id">Escola *</label>
                  {currentUserProfile?.school_adm ? (
                    <input
                      type="text"
                      value={escolas.length > 0 ? escolas[0].nome : 'Escola não encontrada'}
                      disabled
                      className="disabled-input"
                    />
                  ) : (
                    <select
                      id="school_id"
                      value={formData.school_id}
                      onChange={(e) => setFormData({...formData, school_id: e.target.value})}
                      className={formErrors.school_id ? 'error' : ''}
                    >
                      <option value="">Selecione uma escola</option>
                      {escolas.map(escola => (
                        <option key={escola.id} value={escola.id}>{escola.nome}</option>
                      ))}
                    </select>
                  )}
                  {formErrors.school_id && <span className="error-message">{formErrors.school_id}</span>}
                  {escolas.length === 0 && (
                    <small style={{color: '#dc2626'}}>Nenhuma escola encontrada</small>
                  )}
                </div>
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  />
                  <span className="checkmark"></span>
                  Usuário ativo
                </label>
              </div>

              {formErrors.submit && (
                <div className="error-message submit-error">{formErrors.submit}</div>
              )}

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closeModal}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary" disabled={submitLoading}>
                  {submitLoading ? 'Salvando...' : (editingUser ? 'Atualizar' : 'Criar Usuário')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Usuarios;
